<template>
    <template v-if="player.uuid">
        <template v-if="accessGranted">
            <PlayerBody
                class="min-h-screen"
                v-if="!playerNotReleased"
                @updateIframeHeight="updateHeight"
            />
            <PlayerCounter v-else />
        </template>

        <AccessNotGranted v-else :cartUrls="cartUrls" :logo="player.logo" />
    </template>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PlayerBody from "@/views/tools/player/PlayerBody.vue";
import { statsService } from "@/shared/statistics/stats.service";
import { setFontFamily, updateIframeHeight } from "@/shared/utils";
import { preparePlayer } from "@/shared/projects/prepareData";
import { playerCartService } from "@/apps/player/services/player-cart.service";
import AccessNotGranted from "@/views/shared/AccessNotGranted.vue";
import PlayerCounter from "@/views/tools/player/PlayerCounter.vue";
import { useAsyncVuexModules } from "@/store/useAsyncVuexModules";
import playerViewModule from "@/views/tools/player/store/player-view.store";
import { usePreviewLanguage } from "@/shared/utils/router/usePreviewLanguage";
import { projectsService } from "@/shared/projects/projects.service";
import { playerStatsService } from "@/apps/player/services/player-stats.service";

export default {
    name: "PlayersPage",
    components: {
        PlayerBody,
        AccessNotGranted,
        PlayerCounter,
    },
    setup() {
        useAsyncVuexModules(["player/view", playerViewModule]);
        const { setLanguageIfNoQuery } = usePreviewLanguage();
        return { setLanguageIfNoQuery };
    },
    data() {
        return {
            accessGranted: false,
            cartUrls: [],
            windowWidth: window.innerWidth,
            currentFont: "Inter",
            waiting: false,
        };
    },
    computed: {
        ...mapState("player/view", ["player"]),
        playerNotReleased() {
            return this.player.releaseAt && new Date(this.player.releaseAt) > new Date();
        },
    },
    async mounted() {
        try {
            const player = await this.loadPlayer(this.$route.params.projectUuid);

            //in case there is no lang in query chose project ownwer's language
            this.setLanguageIfNoQuery(player?.user?.language);

            this.setPlayer(player);

            //redirect to before release link
            if (
                this.player.releaseAt &&
                new Date(this.player.releaseAt) > new Date() &&
                this.player.beforeReleaseLink
            ) {
                window.location.href = this.player.beforeReleaseLink;
                return;
            }

            //guard access if protected by easycart products
            if (this.player.ecProductIds.length) {
                const data = await playerCartService.getECProductAccess(this.player.ecProductIds);
                this.cartUrls = data.productsUrls;
                this.accessGranted = data.hasAccess;
                if (this.accessGranted) {
                    this.saveVisit();
                }
            } else {
                this.accessGranted = true;
            }

            await this.adjustIframe();
            await this.$nextTick();

            document.title = this.player.pageTitle;
            const favicon = document.querySelector('link[rel="icon"]');
            if (favicon) {
                favicon.href = this.player.pageIcon;
            }

            window.addEventListener("resize", this.resizeIframe);

            await this.adjustIframe();
            await this.saveStat();
        } catch {
            /* eslint-disable-next-line no-empty */
        }
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.resizeIframe);
    },
    methods: {
        ...mapMutations("player/view", ["setPlayer"]),
        async loadPlayer(uuid) {
            this.waiting = true;

            const player = await projectsService.getOneForRender({ tool: "player", uuid });

            this.waiting = false;

            return preparePlayer(player);
        },
        async adjustIframe() {
            this.currentFont = setFontFamily(this.player.fontFamily, this.currentFont);
            await this.$nextTick();
            setTimeout(() => {
                this.updateHeight();
            });
        },
        updateHeight() {
            updateIframeHeight("player", this.player.uuid);
        },
        resizeIframe() {
            if (window.innerWidth !== this.windowWidth) {
                this.adjustIframe();
                this.windowWidth = window.innerWidth;
            }
        },
        async saveStat() {
            const { id, uuid } = this.player;
            await statsService.savePlayerStats(id, uuid);
        },
        saveVisit() {
            playerStatsService.recordVisit(this.player.uuid);
        },
    },
};
</script>
